import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Package, Truck, MapPin, CheckCircle, Clock, Plane, Building2, Home, Bell, Mail, Phone, MessageSquare, Copy, ExternalLink, AlertCircle, Calendar, User, Scale, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/Button';
type ShipmentStatus = 'processing' | 'in_transit' | 'arrived_lagos' | 'out_for_delivery' | 'delivered' | 'interstate_transit';
interface TrackingStep {
  id: string;
  title: string;
  description: string;
  date?: string;
  time?: string;
  location?: string;
  completed: boolean;
  current: boolean;
  icon: React.ElementType;
}
interface ShipmentData {
  trackingId: string;
  status: ShipmentStatus;
  origin: string;
  destination: string;
  estimatedDelivery: string;
  actualDelivery?: string;
  packageType: string;
  weight: string;
  recipientName: string;
  recipientPhone: string;
  hasInterstate: boolean;
  interstateDestination?: string;
  steps: TrackingStep[];
}
const SAMPLE_SHIPMENT: ShipmentData = {
  trackingId: 'LGS-US-987654321',
  status: 'out_for_delivery',
  origin: 'New York, NY, USA',
  destination: 'Lagos, Nigeria',
  estimatedDelivery: 'Feb 8, 2026',
  packageType: 'Electronics',
  weight: '5.5 lbs',
  recipientName: 'Adaeze O****wo',
  recipientPhone: '+234 *** *** **78',
  hasInterstate: false,
  steps: [{
    id: '1',
    title: 'Package Received',
    description: 'Received at US warehouse',
    date: 'Jan 28',
    time: '2:30 PM',
    location: 'New York, NY',
    completed: true,
    current: false,
    icon: Package
  }, {
    id: '2',
    title: 'In Transit',
    description: 'Departed from US',
    date: 'Jan 30',
    time: '8:15 AM',
    location: 'JFK Airport',
    completed: true,
    current: false,
    icon: Plane
  }, {
    id: '3',
    title: 'Arrived Lagos',
    description: 'Cleared customs',
    date: 'Feb 3',
    time: '11:45 AM',
    location: 'Lagos',
    completed: true,
    current: false,
    icon: Building2
  }, {
    id: '4',
    title: 'Out for Delivery',
    description: 'On the way',
    date: 'Feb 5',
    time: '9:00 AM',
    location: 'Lagos',
    completed: false,
    current: true,
    icon: Truck
  }, {
    id: '5',
    title: 'Delivered',
    description: 'Delivered successfully',
    completed: false,
    current: false,
    icon: Home
  }]
};
const STATUS_LABELS: Record<ShipmentStatus, {
  label: string;
  color: string;
  bgColor: string;
}> = {
  processing: {
    label: 'Processing',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100'
  },
  in_transit: {
    label: 'In Transit',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100'
  },
  arrived_lagos: {
    label: 'Arrived Lagos',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100'
  },
  out_for_delivery: {
    label: 'Out for Delivery',
    color: 'text-brand-700',
    bgColor: 'bg-brand-100'
  },
  delivered: {
    label: 'Delivered',
    color: 'text-success',
    bgColor: 'bg-success-light'
  },
  interstate_transit: {
    label: 'Interstate',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100'
  }
};
interface TrackingPageProps {
  onBack?: () => void;
}
export function TrackingPage({
  onBack
}: TrackingPageProps) {
  const [trackingId, setTrackingId] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [shipment, setShipment] = useState<ShipmentData | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifySms, setNotifySms] = useState(false);
  const [copied, setCopied] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const handleSearch = async () => {
    if (!trackingId.trim()) return;
    setIsSearching(true);
    setNotFound(false);
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (trackingId.toUpperCase().includes('LGS') || trackingId.length > 5) {
      setShipment({
        ...SAMPLE_SHIPMENT,
        trackingId: trackingId.toUpperCase()
      });
    } else {
      setNotFound(true);
      setShipment(null);
    }
    setIsSearching(false);
  };
  const handleCopyTrackingId = () => {
    if (shipment) {
      navigator.clipboard.writeText(shipment.trackingId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  return <div className="min-h-screen bg-gray-50 py-6 sm:py-12" data-id="element-1227">
      <div className="max-w-4xl mx-auto px-4" data-id="element-1228">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-6 sm:mb-10" data-id="element-1229">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-brand-50 text-brand-600 text-xs sm:text-sm font-medium mb-3 sm:mb-4" data-id="element-1230">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-1231" />
            Real-Time Tracking
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-3" data-id="element-1232">
            Track Your Shipment
          </h1>
          <p className="text-gray-600 text-sm sm:text-base max-w-xl mx-auto" data-id="element-1233">
            Enter your tracking ID to see the current status of your package.
          </p>
        </motion.div>

        {/* Search Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-4 sm:p-8 shadow-sm mb-6 sm:mb-8" data-id="element-1234">
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4" data-id="element-1235">
            <div className="flex-1 relative" data-id="element-1236">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1237" />
              <input type="text" value={trackingId} onChange={e => setTrackingId(e.target.value)} onKeyPress={handleKeyPress} placeholder="Enter tracking ID" className="w-full pl-12 pr-4 py-3.5 sm:py-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base sm:text-lg" data-id="element-1238" />
            </div>
            <Button size="lg" onClick={handleSearch} disabled={isSearching || !trackingId.trim()} className="gap-2 h-12 sm:h-14 whitespace-nowrap" data-id="element-1239">
              {isSearching ? <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1240" />
                  <span className="hidden sm:inline" data-id="element-1241">Searching...</span>
                </> : <>
                  <Search className="w-5 h-5" data-id="element-1242" />
                  <span data-id="element-1243">Track</span>
                </>}
            </Button>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-3 flex items-center gap-2 flex-wrap" data-id="element-1244">
            <span className="text-gray-400" data-id="element-1245">Example:</span>
            <code className="bg-gray-100 px-2 py-1 rounded text-brand font-mono text-xs" data-id="element-1246">
              LGS-US-123456789
            </code>
          </p>
        </motion.div>

        {/* Not Found */}
        <AnimatePresence data-id="element-1247">
          {notFound && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="bg-red-50 border border-red-200 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 text-center" data-id="element-1248">
              <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-400 mx-auto mb-2 sm:mb-3" data-id="element-1249" />
              <h3 className="font-semibold text-red-800 mb-1 sm:mb-2 text-sm sm:text-base" data-id="element-1250">
                Tracking ID Not Found
              </h3>
              <p className="text-red-600 text-xs sm:text-sm" data-id="element-1251">
                Please check the ID and try again.
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Tracking Results */}
        <AnimatePresence data-id="element-1252">
          {shipment && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="space-y-4 sm:space-y-6" data-id="element-1253">
              {/* Status Header */}
              <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-sm" data-id="element-1254">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-4 sm:mb-6" data-id="element-1255">
                  <div data-id="element-1256">
                    <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2" data-id="element-1257">
                      <h2 className="text-base sm:text-xl font-bold text-gray-900 font-mono" data-id="element-1258">
                        {shipment.trackingId}
                      </h2>
                      <button onClick={handleCopyTrackingId} className="p-1.5 sm:p-2 text-gray-400 active:text-brand active:bg-brand-50 rounded-lg transition-colors" data-id="element-1259">
                        {copied ? <CheckCircle className="w-4 h-4 text-success" data-id="element-1260" /> : <Copy className="w-4 h-4" data-id="element-1261" />}
                      </button>
                    </div>
                    <p className="text-gray-500 text-xs sm:text-sm" data-id="element-1262">
                      {shipment.origin} → {shipment.destination}
                    </p>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full ${STATUS_LABELS[shipment.status].bgColor} ${STATUS_LABELS[shipment.status].color} font-medium text-xs sm:text-sm self-start sm:self-auto`} data-id="element-1263">
                    <span className="relative flex h-2 w-2" data-id="element-1264">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" data-id="element-1265"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-current" data-id="element-1266"></span>
                    </span>
                    {STATUS_LABELS[shipment.status].label}
                  </div>
                </div>

                {/* Timeline - Simplified for mobile */}
                <div className="relative" data-id="element-1267">
                  {shipment.steps.map((step, index) => <div key={step.id} className="flex gap-3 sm:gap-4 pb-4 sm:pb-6 last:pb-0" data-id="element-1268">
                      <div className="flex flex-col items-center" data-id="element-1269">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${step.completed ? 'bg-success border-success text-white' : step.current ? 'bg-brand border-brand text-white ring-2 sm:ring-4 ring-brand-100' : 'bg-gray-100 border-gray-300 text-gray-400'}`} data-id="element-1270">
                          {step.completed ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1271" /> : <step.icon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-1272" />}
                        </div>
                        {index < shipment.steps.length - 1 && <div className={`w-0.5 flex-1 mt-1 sm:mt-2 min-h-[20px] ${step.completed ? 'bg-success' : 'bg-gray-200'}`} data-id="element-1273" />}
                      </div>

                      <div className="flex-1 pt-0.5 sm:pt-1 min-w-0" data-id="element-1274">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-1" data-id="element-1275">
                          <h3 className={`font-semibold text-sm sm:text-base truncate ${step.current ? 'text-brand' : step.completed ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-1276">
                            {step.title}
                          </h3>
                          {step.date && <span className="text-xs text-gray-500 flex-shrink-0" data-id="element-1277">
                              {step.date}
                            </span>}
                        </div>
                        <p className={`text-xs sm:text-sm ${step.completed || step.current ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-1278">
                          {step.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Shipment Details - Collapsible on mobile */}
              <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-1279">
                <button onClick={() => setDetailsExpanded(!detailsExpanded)} className="sm:hidden w-full bg-secondary-900 p-4 text-white flex items-center justify-between" data-id="element-1280">
                  <h3 className="font-semibold flex items-center gap-2" data-id="element-1281">
                    <Package className="w-5 h-5" data-id="element-1282" />
                    Shipment Details
                  </h3>
                  {detailsExpanded ? <ChevronUp className="w-5 h-5" data-id="element-1283" /> : <ChevronDown className="w-5 h-5" data-id="element-1284" />}
                </button>

                <div className="hidden sm:block bg-secondary-900 p-4 text-white" data-id="element-1285">
                  <h3 className="font-semibold flex items-center gap-2" data-id="element-1286">
                    <Package className="w-5 h-5" data-id="element-1287" />
                    Shipment Details
                  </h3>
                </div>

                <AnimatePresence initial={false} data-id="element-1288">
                  {(detailsExpanded || typeof window !== 'undefined' && window.innerWidth >= 640) && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="sm:!h-auto sm:!opacity-100 overflow-hidden" data-id="element-1289">
                      <div className="p-4 sm:p-6 grid grid-cols-2 gap-4 sm:gap-6" data-id="element-1290">
                        <div data-id="element-1291">
                          <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-1292">
                            Origin
                          </span>
                          <span className="text-gray-900 font-medium text-sm sm:text-base flex items-center gap-1 sm:gap-2" data-id="element-1293">
                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-brand flex-shrink-0" data-id="element-1294" />
                            <span className="truncate" data-id="element-1295">{shipment.origin}</span>
                          </span>
                        </div>
                        <div data-id="element-1296">
                          <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-1297">
                            Destination
                          </span>
                          <span className="text-gray-900 font-medium text-sm sm:text-base flex items-center gap-1 sm:gap-2" data-id="element-1298">
                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-success flex-shrink-0" data-id="element-1299" />
                            <span className="truncate" data-id="element-1300">
                              {shipment.destination}
                            </span>
                          </span>
                        </div>
                        <div data-id="element-1301">
                          <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-1302">
                            Est. Delivery
                          </span>
                          <span className="text-gray-900 font-medium text-sm sm:text-base flex items-center gap-1 sm:gap-2" data-id="element-1303">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-brand flex-shrink-0" data-id="element-1304" />
                            {shipment.estimatedDelivery}
                          </span>
                        </div>
                        <div data-id="element-1305">
                          <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-1306">
                            Package
                          </span>
                          <span className="text-gray-900 font-medium text-sm sm:text-base" data-id="element-1307">
                            {shipment.packageType}
                          </span>
                        </div>
                        <div data-id="element-1308">
                          <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-1309">
                            Weight
                          </span>
                          <span className="text-gray-900 font-medium text-sm sm:text-base flex items-center gap-1 sm:gap-2" data-id="element-1310">
                            <Scale className="w-3 h-3 sm:w-4 sm:h-4 text-brand flex-shrink-0" data-id="element-1311" />
                            {shipment.weight}
                          </span>
                        </div>
                        <div data-id="element-1312">
                          <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider block mb-1" data-id="element-1313">
                            Recipient
                          </span>
                          <span className="text-gray-900 font-medium text-sm sm:text-base flex items-center gap-1 sm:gap-2" data-id="element-1314">
                            <User className="w-3 h-3 sm:w-4 sm:h-4 text-brand flex-shrink-0" data-id="element-1315" />
                            <span className="truncate" data-id="element-1316">
                              {shipment.recipientName}
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="border-t border-gray-100 p-4 bg-gray-50" data-id="element-1317">
                        <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto" data-id="element-1318">
                          <MessageSquare className="w-4 h-4" data-id="element-1319" />
                          Contact Support
                        </Button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3" data-id="element-1320">
                <Button variant="outline" className="gap-2 h-12 text-sm" data-id="element-1321">
                  <ExternalLink className="w-4 h-4" data-id="element-1322" />
                  <span className="hidden sm:inline" data-id="element-1323">Share</span> Link
                </Button>
                <Button variant="outline" className="gap-2 h-12 text-sm" data-id="element-1324">
                  <Package className="w-4 h-4" data-id="element-1325" />
                  Report Issue
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Empty State */}
        {!shipment && !notFound && !isSearching && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-center py-8 sm:py-12" data-id="element-1326">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4" data-id="element-1327">
              <Package className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" data-id="element-1328" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2" data-id="element-1329">
              Enter your tracking ID
            </h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto" data-id="element-1330">
              Your tracking ID was sent in your confirmation email.
            </p>
          </motion.div>}
      </div>
    </div>;
}